import { getBlob } from "../Services/ApiBase";
import { API_ENDPOINTS } from "../Services/ApiEndPoints";
import { buildProjectWorkItemsExportQueryParams } from "../Redux/Thunks/projectManagementThunkUtils";
import { getApiErrorMessage } from "./apiErrorMessage";

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return null;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1].trim();
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain ? plain[1].trim().replace(/^["']|["']$/g, "") : null;
}

async function blobToErrorMessage(blob) {
  try {
    const text = await blob.text();
    const j = JSON.parse(text);
    return j.message || j.error || j.data?.message || text || "Export failed";
  } catch {
    return "Export failed";
  }
}

/**
 * GET /projects/:projectId/work-items/export?format=csv|xlsx and save the file.
 * Optional `filterBody` matches project work-items list filters (serialized like list GET).
 *
 * @param {string} projectId — route param (UUID)
 * @param {"csv"|"xlsx"} format
 * @param {Record<string, unknown>} [filterBody] — optional filter fields (typeIds, statusIds, search, …).
 */
export async function downloadProjectWorkItemsExport(projectId, format, filterBody = null) {
  if (!projectId) {
    throw new Error("Missing project id");
  }
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  const extra =
    filterBody && typeof filterBody === "object" && Object.keys(filterBody).length > 0
      ? buildProjectWorkItemsExportQueryParams(filterBody)
      : {};

  try {
    const response = await getBlob(API_ENDPOINTS.PROJECTS.EXPORT_WORK_ITEMS(projectId), {
      format: fmt,
      ...extra,
    });

    const blob = response.data;
    const contentType = String(
      response.headers?.["content-type"] || response.headers?.["Content-Type"] || ""
    );

    if (contentType.includes("application/json")) {
      throw new Error(await blobToErrorMessage(blob));
    }

    const fallback =
      fmt === "xlsx" ? `work-items-${projectId}.xlsx` : `work-items-${projectId}.csv`;
    const cd =
      response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
    const filename = parseFilenameFromContentDisposition(cd) || fallback;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (err === "Request canceled") throw err;
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const msg = await blobToErrorMessage(data);
      throw new Error(msg, { cause: err });
    }
    throw new Error(getApiErrorMessage(err, "Export failed"), { cause: err });
  }
}
